<script lang="ts" setup>
import { useThemeStore } from '@/stores/themeStore'
import { buildDefaultChartOptions, noExponents } from '@/utils'
import { useTzDate } from '@/composable/useTzDate'
import {
  LineSeries,
  createChart,
  type CreatePriceLineOptions,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'

const emit = defineEmits(["mounted"])

const chartContainer = useTemplateRef('mlwChartContainer')
let chart = null as IChartApi | null
const availableSeries = ref<string[]>([])
let series = {} as Record<string, ISeriesApi<any>> // eslint-disable-line
const priceLine = {} as Record<string, IPriceLine>

interface LegendItemState {
  label: string
  color: string
  value: number | null
  visible: boolean
}

const legendState = ref<Record<string, LegendItemState>>({})

const formatLegendValue = (v: number | null | undefined): string => {
  if (v == null) return '—'
  return v.toFixed(precision.value ?? 5)
}

const refreshLegend = () => {
  if (!props.legend || props.legend.length === 0) return
  const next: Record<string, LegendItemState> = {}
  for (const item of props.legend) {
    const s = series[item.name]
    if (!s) continue
    const opts = s.options()
    const data = s.data()
    const last = data[data.length - 1] as { value?: number } | undefined
    next[item.name] = {
      label: item.label,
      color: (opts.color as string) ?? '#999',
      value: last?.value ?? null,
      visible: opts.visible !== false,
    }
  }
  legendState.value = next
}

const toggleLegendItem = (name: string) => {
  const s = series[name]
  if (!s) return
  const visible = s.options().visible !== false
  setSeriesVisibility(name, !visible)
  refreshLegend()
}

const themeStore = useThemeStore()
const { offsetMs } = useTzDate()

interface LegendItemConfig {
  name: string
  label: string
}

const props = defineProps<{
  height: string
  priceLineColor?: string
  minMove?: number | null
  yFormat?: string | null
  legend?: LegendItemConfig[]
}>()

const priceLineColor = computed(() => {
  return props.priceLineColor ?? '#26a69a'
})

const precision = computed(() => noExponents(props.minMove ?? 0)?.split('.')[1]?.length)
const initChart = () => {
  if (!chartContainer.value) {
    return
  }
  chart = createChart(chartContainer.value, buildDefaultChartOptions(props.yFormat ?? '1s', offsetMs))
}

const getScreenshot = (callback: BlobCallback) => {
  return chart?.takeScreenshot(true).toBlob(callback)
}

const reset = () => {
  chart?.timeScale().fitContent()
}

watch(
  () => themeStore.isDark,
  () => {
    chart?.applyOptions(buildDefaultChartOptions(props.yFormat ?? '1s', offsetMs))
  },
)

const buildSeriesOptions = () => {
  const mm = props.minMove ?? 0.01
  const res = {
    priceFormat: {
      type: 'price' as "price",
      precision: precision.value,
      minMove: mm,
    },
  }
  return res
}

const setSeriesVisibility = (name: string, visible: boolean) => {
  if (!chart || !(name in series)) {
    return
  }
  series[name]?.applyOptions({
    visible: visible,
  })
  refreshLegend()
}

const setSeriesColor = (name: string, color: string) => {
  if (!chart || !(name in series)) {
    return
  }
  series[name]?.applyOptions({
    color: color,
  })
  refreshLegend()
}

const setPriceLine = (seriesName: string, name: string, title: string, price: number, color?: string | null | undefined) => {
  if (!chart || !series[seriesName]) {
    return
  }
  if (!priceLine[name]) {
    const myPriceLine = {
      price: price,
      color: color ?? priceLineColor.value,
      lineWidth: 2,
      lineStyle: 2,
      axisLabelVisible: true,
      title: title,
    } as CreatePriceLineOptions

    console.debug(`add price line ${name} from ${seriesName}`)
    priceLine[name] = series[seriesName].createPriceLine(myPriceLine);
  } else {
    console.debug(`updating price line ${name} from ${seriesName}`)
    priceLine[name].applyOptions({
      price: price,
      color: color ?? priceLineColor.value,
    })
  }
}

const removePriceLine = (seriesName: string, name: string) => {
  if (!chart || !series[seriesName]) {
    return
  }
  if (priceLine[name]) {
    console.debug(`deleting price line ${name} from ${seriesName}`)
    series[seriesName].removePriceLine(priceLine[name])
    delete priceLine[name]
  }
}

const addSeries = (name: string, color?: string | null | undefined) => {
  if (!chart || name in series) {
    return
  }
  const opts = {
    ...buildSeriesOptions(),
    baseLineColor: color ?? '#B2B5BE',
    priceLineColor: color ?? '',
    color: color ?? '#2196f3',
    topLineColor: color ?? 'rgba(38, 166, 154, 1)',
  }
  availableSeries.value.push(name)
  const baselineSeries = chart.addSeries(LineSeries, opts)
  series[name] = baselineSeries
  refreshLegend()
}

// eslint-disable-next-line
const updateSeries = (name: string, data: any[]) => {
  if (!(name in series)) {
    return
  }
  const fmt = data.map(([ts, v]) => ({
    time: ts as UTCTimestamp,
    value: v,
  }))

  if (series[name]?.data().length == 0) {
    series[name]?.setData(fmt)
  }
  refreshLegend()
}

watch(
  () => props.minMove,
  () => {
    Object.entries(series).forEach(([key]) => {
      const opts = series[key]?.options
      const newOpts = {
        ...opts,
        ...buildSeriesOptions(),
      }
      series[key]?.applyOptions(newOpts)
    })
  },
)

// eslint-disable-next-line
const pushIntoSeries = (name: string, data: any) => {
  if (!(name in series)) {
    return
  }
  const fmt = {
    time: data[0] as UTCTimestamp,
    value: data[1],
  }
  series[name]?.update(fmt)
  refreshLegend()
}

const getSeriesData = (name: string) => {
  return series[name]?.data()
}

defineExpose({
  getScreenshot,
  reset,
  getSeriesData,
  addSeries,
  updateSeries,
  pushIntoSeries,
  setSeriesColor,
  setSeriesVisibility,
  setPriceLine,
  removePriceLine,
  availableSeries,
})

onMounted(async () => {
  initChart()
  await nextTick()
  emit('mounted')
})

onBeforeUnmount(() => {
  chart?.remove();
})
</script>

<template>
  <div class="lw-chart relative" ref="mlwChartContainer" :style="`height: ${props.height}`">
    <div v-if="props.legend && props.legend.length"
      class="absolute top-2 left-2 flex flex-col gap-1 pointer-events-none">
      <template v-for="item in props.legend" :key="item.name">
        <div v-if="legendState[item.name]?.visible !== false"
          class="flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-black/40 backdrop-blur-sm text-white text-xs cursor-pointer pointer-events-auto select-none"
          @click="toggleLegendItem(item.name)">
          <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: legendState[item.name]?.color }" />
          <span class="font-mono whitespace-nowrap">
            {{ $t(item.label) }}: {{ formatLegendValue(legendState[item.name]?.value) }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<style>
#tv-attr-logo {
  display: none;
}
</style>
